import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Search, Users, Sparkles, TrendingUp, Filter,
  Mail, Phone, Globe, X, MessageSquare, Flame, Download, UserPlus, Copy, Send,
  LayoutGrid, Table as TableIcon, CheckSquare, Square, Bell, Tag,
} from "lucide-react";
import { http } from "@/lib/api";
import LeadsAnalytics from "@/components/LeadsAnalytics";
import AdvisorPanel from "@/components/AdvisorPanel";
import KanbanBoard from "@/components/KanbanBoard";
import NotesTagsPanel from "@/components/NotesTagsPanel";
import TestimonialsAdmin from "@/components/TestimonialsAdmin";
import { REPLY_TEMPLATES, fillTemplate } from "@/lib/replyTemplates";
import { SLABadge, tagColor } from "@/lib/uiHelpers";
import { useLeadNotifier } from "@/lib/useLeadNotifier";
import ReplyPreviewModal from "@/components/ReplyPreviewModal";
import TeamChat from "@/components/TeamChat";
import ImportLeadsModal from "@/components/ImportLeadsModal";
import ReassignRequestModal from "@/components/ReassignRequestModal";
import ApprovalsPanel from "@/components/ApprovalsPanel";
import BlogStudio from "@/components/BlogStudio";

const STATUSES = ["New", "Contacted", "In Progress", "Closed"];
const STATUS_CLASS = {
  New: "status-new",
  Contacted: "status-contacted",
  "In Progress": "status-progress",
  Closed: "status-closed",
};

const isUrgent = (l) => l.timeline === "Under 3 months";

function toCSV(rows) {
  const cols = ["created_at", "name", "email", "phone", "target_country", "visa_type", "timeline", "status", "advisor", "notes"];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => escape(r[c])).join(",")).join("\n");
  return header + "\n" + body;
}

export default function CrmDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [countryFilter, setCountryFilter] = useState("All");
  const [tagFilter, setTagFilter] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("ns_crm_view") || "table");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [me, setMe] = useState({
    email: localStorage.getItem("ns_email") || "",
    role: localStorage.getItem("ns_role") || "staff",
    name: localStorage.getItem("ns_name") || "",
    tier: localStorage.getItem("ns_tier") || "ops",
    staff_role: localStorage.getItem("ns_staff_role") || "",
  });
  const isAdvisor = me.role === "advisor";
  const canReassign = me.tier === "founder" || me.tier === "ops";
  const [importOpen, setImportOpen] = useState(false);
  const { unseen, clear: clearUnseen } = useLeadNotifier(leads);

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "All") params.status = statusFilter;
      if (countryFilter !== "All") params.country = countryFilter;
      if (tagFilter !== "All") params.tag = tagFilter;
      const { data } = await http.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("ns_token");
        nav("/crm/login");
      } else {
        console.error("Failed to load leads:", e);
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, countryFilter, tagFilter, nav]);

  const fetchAdvisors = useCallback(async () => {
    try {
      const { data } = await http.get("/advisors");
      setAdvisors(data.advisors || []);
    } catch (e) {
      console.error("Failed to load advisors:", e);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await http.get("/tags");
      setTagsList(data.tags || []);
    } catch (e) {
      console.error("Failed to load tags:", e);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("ns_token")) {
      nav("/crm/login");
      return;
    }
    fetchLeads();
    fetchAdvisors();
    fetchTags();
    const poll = setInterval(() => { fetchLeads(); fetchTags(); }, 15000);
    return () => clearInterval(poll);
  }, [fetchLeads, fetchAdvisors, fetchTags, nav]);

  const filtered = useMemo(() => {
    if (!q) return leads;
    const needle = q.toLowerCase();
    return leads.filter(
      (l) =>
        l.name?.toLowerCase().includes(needle) ||
        l.email?.toLowerCase().includes(needle) ||
        l.target_country?.toLowerCase().includes(needle) ||
        l.visa_type?.toLowerCase().includes(needle) ||
        l.advisor?.toLowerCase().includes(needle)
    );
  }, [leads, q]);

  const metrics = useMemo(() => {
    const today = new Date().toDateString();
    const newToday = leads.filter((l) => new Date(l.created_at).toDateString() === today).length;
    const inProgress = leads.filter((l) => l.status === "In Progress" || l.status === "Contacted").length;
    const closed = leads.filter((l) => l.status === "Closed").length;
    return {
      total: leads.length,
      newToday,
      inProgress,
      conversion: leads.length ? Math.round((closed / leads.length) * 100) : 0,
    };
  }, [leads]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await http.patch(`/leads/${id}/status`, { status });
      setLeads((cur) => cur.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Status set to ${status}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const updateAdvisor = async (id, advisor) => {
    try {
      const { data } = await http.patch(`/leads/${id}/advisor`, { advisor: advisor || null });
      setLeads((cur) => cur.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success(advisor ? `Assigned to ${advisor}` : "Unassigned");
    } catch (e) {
      toast.error("Failed to assign advisor");
    }
  };

  const bulkUpdate = async ({ status, advisor }) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const results = await Promise.all(ids.map((id) => {
        if (status) return http.patch(`/leads/${id}/status`, { status });
        if (advisor !== undefined) return http.patch(`/leads/${id}/advisor`, { advisor: advisor || null });
        return null;
      }));
      const map = new Map(results.filter(Boolean).map((r) => [r.data.id, r.data]));
      setLeads((cur) => cur.map((l) => map.get(l.id) || l));
      toast.success(`Updated ${ids.length} lead${ids.length === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error("Bulk update failed");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (rows) => {
    setSelectedIds((cur) => {
      if (rows.every((r) => cur.has(r.id))) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  const exportCSV = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nextshore-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"}`);
  };

  const logout = () => {
    localStorage.removeItem("ns_token");
    localStorage.removeItem("ns_email");
    localStorage.removeItem("ns_role");
    localStorage.removeItem("ns_name");
    localStorage.removeItem("ns_tier");
    localStorage.removeItem("ns_staff_role");
    nav("/crm/login");
  };

  const countries = ["All", ...Array.from(new Set(leads.map((l) => l.target_country))).filter(Boolean)];
  const advisorNames = advisors.map((a) => (typeof a === "string" ? a : a.name));

  return (
    <div data-testid="crm-dashboard" className="min-h-screen bg-[#F4F6F9] text-[#0F172A] flex">
      <aside className="hidden md:flex w-64 bg-[#0A1220] text-slate-200 flex-col">
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <img src="/logo.png" alt="NS-I" className="h-10 w-auto rounded" />
          <div className="font-serif text-lg leading-tight font-bold">NS-I</div>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          <button
            onClick={clearUnseen}
            data-testid="nav-leads-link"
            className="w-full px-3 py-2 rounded-lg bg-[#162847] text-white flex items-center justify-between text-sm"
          >
            <span className="flex items-center gap-3"><Users className="w-4 h-4" /> Leads</span>
            {unseen > 0 && (
              <span data-testid="unseen-dot" className="inline-flex items-center gap-1 rounded-full bg-red-500 text-white px-2 py-0.5 text-[10px] font-bold animate-pulse">
                <Bell className="w-2.5 h-2.5" /> {unseen}
              </span>
            )}
          </button>
        </nav>
        <div className="px-6 py-5 border-t border-white/10 text-xs">
          <div className="text-slate-400">Signed in as</div>
          <div className="truncate font-semibold">{me.name || me.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#C9A961] mt-0.5">{me.role}</div>
          <button
            data-testid="crm-logout-btn"
            onClick={logout}
            className="mt-3 inline-flex items-center gap-2 text-slate-300 hover:text-[#C9A961]"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="px-6 lg:px-10 py-6 border-b bg-white flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl leading-tight">Leads</h1>
            <p className="text-sm text-slate-500">Captured by Izzy · updated in real time</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="crm-import-csv-btn"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 px-4 py-2 text-sm font-semibold"
            >
              <Download className="w-4 h-4 rotate-180" /> Import CSV
            </button>
            <button
              data-testid="crm-export-csv-btn"
              onClick={exportCSV}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 px-4 py-2 text-sm font-semibold"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              data-testid="crm-refresh-btn"
              onClick={fetchLeads}
              className="text-xs uppercase tracking-widest text-[#0B1E3F] hover:text-[#C9A961]"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ["Total Leads", metrics.total, Users, "bg-blue-50 text-blue-700"],
            ["New Today", metrics.newToday, Sparkles, "bg-amber-50 text-amber-700"],
            ["In Progress", metrics.inProgress, Filter, "bg-purple-50 text-purple-700"],
            ["Closed Rate", `${metrics.conversion}%`, TrendingUp, "bg-emerald-50 text-emerald-700"],
          ].map(([label, val, Icon, cls]) => (
            <div key={label} data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${cls} mb-3`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="text-2xl font-serif">{val}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
            </div>
          ))}
        </div>

        <div className="px-6 lg:px-10">
          <LeadsAnalytics leads={leads} />
          <ApprovalsPanel tier={me.tier} />
          <BlogStudio tier={me.tier} />
          {!isAdvisor && <AdvisorPanel advisors={advisors} leads={leads} onChange={setAdvisors} />}
          {!isAdvisor && <TestimonialsAdmin />}
        </div>

        <div className="px-6 lg:px-10 pb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <input
              data-testid="crm-leads-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, country, advisor…"
              className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#0B1E3F]"
            />
          </div>
          <select
            data-testid="crm-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
          >
            <option>All</option>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select
            data-testid="crm-filter-country"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm"
          >
            {countries.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select
            data-testid="crm-filter-tag"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm inline-flex items-center"
          >
            <option value="All">All tags</option>
            {tagsList.map((t) => <option key={t} value={t}>#{t}</option>)}
          </select>
          <div className="inline-flex rounded-xl border border-slate-200 bg-white overflow-hidden">
            <button
              data-testid="view-table-btn"
              onClick={() => { setView("table"); localStorage.setItem("ns_crm_view", "table"); }}
              className={`inline-flex items-center gap-1 px-3 py-2.5 text-xs font-semibold ${view === "table" ? "bg-[#0B1E3F] text-white" : "text-slate-600"}`}
            >
              <TableIcon className="w-3.5 h-3.5" /> Table
            </button>
            <button
              data-testid="view-kanban-btn"
              onClick={() => { setView("kanban"); localStorage.setItem("ns_crm_view", "kanban"); }}
              className={`inline-flex items-center gap-1 px-3 py-2.5 text-xs font-semibold ${view === "kanban" ? "bg-[#0B1E3F] text-white" : "text-slate-600"}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
          </div>
        </div>

        {view === "kanban" ? (
          <div className="px-6 lg:px-10 pb-10">
            <KanbanBoard
              leads={filtered}
              onStatusChange={updateStatus}
              onOpen={setSelected}
            />
          </div>
        ) : (
        <div className="px-6 lg:px-10 pb-10">
          {selectedIds.size > 0 && (
            <div data-testid="bulk-toolbar" className="mb-3 flex flex-wrap items-center gap-3 bg-[#0B1E3F] text-white rounded-2xl px-5 py-3 shadow">
              <span className="text-sm font-semibold">{selectedIds.size} selected</span>
              <select
                data-testid="bulk-status"
                onChange={(e) => { if (e.target.value) { bulkUpdate({ status: e.target.value }); e.target.value = ""; } }}
                defaultValue=""
                className="bg-white text-slate-800 rounded-lg px-3 py-1.5 text-xs"
              >
                <option value="">Change status…</option>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select
                data-testid="bulk-advisor"
                onChange={(e) => { bulkUpdate({ advisor: e.target.value }); e.target.value = ""; }}
                defaultValue=""
                className="bg-white text-slate-800 rounded-lg px-3 py-1.5 text-xs"
              >
                <option value="">Reassign advisor…</option>
                <option value="">Unassigned</option>
                {advisorNames.map((a) => <option key={a}>{a}</option>)}
              </select>
              <button
                data-testid="bulk-clear"
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-xs uppercase tracking-widest text-slate-300 hover:text-white"
              >
                Clear
              </button>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto thin-scroll">
            <table className="w-full text-sm min-w-[1000px]">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 w-8">
                    <button
                      data-testid="select-all-btn"
                      onClick={() => toggleSelectAll(filtered)}
                      className="text-slate-400 hover:text-[#0B1E3F]"
                      aria-label="Select all"
                    >
                      {filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id))
                        ? <CheckSquare className="w-4 h-4" />
                        : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="px-5 py-3">Lead</th>
                  <th className="px-5 py-3">Contact</th>
                  <th className="px-5 py-3">Destination</th>
                  <th className="px-5 py-3">Visa</th>
                  <th className="px-5 py-3">Timeline</th>
                  <th className="px-5 py-3">Advisor</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="text-center py-10 text-slate-400">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-10 text-slate-400">
                    No leads yet — Izzy will capture them here.
                  </td></tr>
                )}
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className={`border-b border-slate-50 hover:bg-slate-50/60 ${isUrgent(l) ? "bg-amber-50/40" : ""} ${selectedIds.has(l.id) ? "bg-blue-50/60" : ""}`}>
                    <td className="px-4 py-4">
                      <button
                        data-testid={`select-row-${l.id}`}
                        onClick={() => toggleSelect(l.id)}
                        className="text-slate-400 hover:text-[#0B1E3F]"
                        aria-label="Select row"
                      >
                        {selectedIds.has(l.id) ? <CheckSquare className="w-4 h-4 text-[#0B1E3F]" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-5 py-4 font-semibold">
                      <div className="flex items-center gap-2 flex-wrap">
                        {l.name}
                        {isUrgent(l) && (
                          <span
                            data-testid={`priority-badge-${l.id}`}
                            title="Urgent timeline: under 3 months"
                            className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                          >
                            <Flame className="w-3 h-3" /> Hot
                          </span>
                        )}
                        {isUrgent(l) && <SLABadge createdAt={l.created_at} />}
                      </div>
                      {(l.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {l.tags.map((t) => {
                            const c = tagColor(t);
                            return (
                              <span key={t} className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: c.bg, color: c.text }}>{t}</span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-600">
                      <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{l.email}</div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5"><Phone className="w-3 h-3" />{l.phone}</div>
                    </td>
                    <td className="px-5 py-4">{l.target_country}</td>
                    <td className="px-5 py-4">{l.visa_type}</td>
                    <td className="px-5 py-4 text-slate-600">
                      {l.timeline}
                      {isUrgent(l) && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-600 font-semibold">Urgent</span>}
                    </td>
                    <td className="px-5 py-4">
                      {canReassign ? (
                        <select
                          data-testid={`crm-advisor-select-${l.id}`}
                          value={l.advisor || ""}
                          onChange={(e) => updateAdvisor(l.id, e.target.value)}
                          className="rounded-lg px-2 py-1 text-xs border border-slate-200 bg-white"
                        >
                          <option value="">Unassigned</option>
                          {advisorNames.map((a) => <option key={a}>{a}</option>)}
                        </select>
                      ) : (
                        <span data-testid={`crm-advisor-readonly-${l.id}`} className="text-xs text-slate-600">{l.advisor || "Unassigned"}</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`crm-lead-status-select-${l.id}`}
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold border ${STATUS_CLASS[l.status] || ""}`}
                      >
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-500">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        data-testid={`crm-view-transcript-btn-${l.id}`}
                        onClick={() => setSelected(l)}
                        className="inline-flex items-center gap-1 text-xs text-[#0B1E3F] hover:text-[#C9A961]"
                      >
                        <MessageSquare className="w-3 h-3" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </main>

      {selected && (
        <LeadDetail
          lead={selected}
          advisors={advisors}
          isAdvisor={isAdvisor}
          canReassign={canReassign}
          me={me}
          onClose={() => setSelected(null)}
          onAssign={(a) => updateAdvisor(selected.id, a)}
          onLogged={(updated) => {
            setLeads((cur) => cur.map((l) => (l.id === updated.id ? updated : l)));
            setSelected(updated);
          }}
        />
      )}

      <ImportLeadsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        canAssign={canReassign}
        onImported={fetchLeads}
      />
    </div>
  );
}

function LeadDetail({ lead, advisors, isAdvisor, canReassign, me, onClose, onAssign, onLogged }) {
  const [tplKey, setTplKey] = useState(lead.visa_type);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const template = REPLY_TEMPLATES[tplKey] || REPLY_TEMPLATES.Work;
  const filled = fillTemplate(template, lead);
  const advisorNames = advisors.map((a) => (typeof a === "string" ? a : a.name));
  const advisorObj = advisors.find((a) => (typeof a === "string" ? a === lead.advisor : a.name === lead.advisor));
  const advisorEmail = advisorObj && typeof advisorObj === "object" ? advisorObj.email : null;

  const logReply = async (method) => {
    try {
      const { data } = await http.post(`/leads/${lead.id}/reply-log`, {
        visa_template: tplKey,
        subject: filled.subject,
        body: filled.body,
        method,
      });
      onLogged?.(data);
    } catch (e) {
      console.error("Reply log failed:", e);
    }
  };

  const copyReply = async () => {
    const text = `Subject: ${filled.subject}\n\n${filled.body}`;
    await navigator.clipboard.writeText(text);
    toast.success("Reply template copied & logged");
    logReply("copy");
  };

  const sendReply = async () => {
    setSending(true);
    try {
      const { data } = await http.post(`/leads/${lead.id}/send-reply`, {
        visa_template: tplKey,
        subject: filled.subject,
        body: filled.body,
      });
      onLogged?.(data);
      toast.success(`Reply sent to ${lead.email}`);
      setPreviewOpen(false);
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  const mailtoLink = `mailto:${encodeURIComponent(lead.email)}?${advisorEmail ? `cc=${encodeURIComponent(advisorEmail)}&` : ""}subject=${encodeURIComponent(filled.subject)}&body=${encodeURIComponent(filled.body)}`;

  return (
    <div
      data-testid="crm-lead-detail-panel"
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full sm:w-[540px] h-full bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Lead detail</div>
            <div className="font-serif text-xl flex items-center gap-2">
              {lead.name}
              {isUrgent(lead) && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"><Flame className="w-3 h-3" /> Hot</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="crm-close-detail-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <Detail label="Email" value={lead.email} />
          <Detail label="Phone" value={lead.phone} />
          <Detail label="Country" value={lead.target_country} />
          <Detail label="Visa Type" value={lead.visa_type} />
          <Detail label="Timeline" value={lead.timeline} />
          <Detail label="Status" value={lead.status} />
        </div>

        {/* Advisor assign */}
        <div className="px-6 pb-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
            <UserPlus className="w-3 h-3" /> Assigned advisor
          </div>
          <select
            data-testid="crm-detail-advisor-select"
            value={lead.advisor || ""}
            onChange={(e) => onAssign(e.target.value)}
            disabled={!canReassign}
            className="w-full rounded-xl px-3 py-2.5 text-sm border border-slate-200 bg-white disabled:bg-slate-50 disabled:text-slate-500"
          >
            <option value="">Unassigned</option>
            {advisorNames.map((a) => <option key={a}>{a}</option>)}
          </select>
          {!canReassign && (
            <button
              data-testid="crm-request-reassign-btn"
              onClick={() => setReassignOpen(true)}
              className="mt-2 text-xs text-[#0B1E3F] hover:text-[#C9A961] underline"
            >
              Request reassignment (needs Ops + Founder approval)
            </button>
          )}
          {advisorEmail && (
            <div className="text-[11px] text-slate-500 mt-1.5">
              Replies will CC <span className="font-semibold text-slate-700">{advisorEmail}</span>
            </div>
          )}
        </div>

        <NotesTagsPanel lead={lead} onUpdate={onLogged} />

        <TeamChat lead={lead} me={me} onUpdate={onLogged} />

        {/* Reply templates */}
        <div className="px-6 pb-6 border-t pt-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">One-click reply template</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.keys(REPLY_TEMPLATES).map((k) => (
              <button
                key={k}
                data-testid={`reply-tpl-${k.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setTplKey(k)}
                className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${tplKey === k ? "bg-[#0B1E3F] text-white border-[#0B1E3F]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0B1E3F]"}`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Subject</div>
            <div className="text-sm text-slate-800 mb-3">{filled.subject}</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Body</div>
            <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-y-auto thin-scroll">{filled.body}</pre>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              data-testid="reply-copy-btn"
              onClick={copyReply}
              className="rounded-xl border border-slate-200 bg-white hover:bg-slate-50 px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 flex-1 min-w-[110px]"
            >
              <Copy className="w-4 h-4" /> Copy
            </button>
            <a
              data-testid="reply-mailto-btn"
              href={mailtoLink}
              onClick={() => logReply("mailto")}
              className="rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 flex-1 min-w-[110px]"
            >
              <Send className="w-4 h-4" /> Mail client
            </a>
            <button
              data-testid="reply-send-btn"
              onClick={() => setPreviewOpen(true)}
              disabled={sending}
              className="rounded-xl bg-[#0B1E3F] hover:bg-[#162A4D] text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 flex-1 min-w-[110px] disabled:opacity-60"
            >
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send now"}
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-t">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Conversation transcript</div>
          <div className="space-y-2">
            {(lead.transcript || []).map((m, i) => (
              <div
                key={`${m.role}-${m.sent_at || m.timestamp || i}-${i}`}
                className={`text-sm px-3 py-2 rounded-lg max-w-[92%] whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#0B1E3F] text-white"
                    : m.role === "advisor"
                    ? "ml-auto bg-[#C9A961]/15 border border-[#C9A961]/40 text-slate-800"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                {m.role === "advisor" && (
                  <div className="text-[10px] uppercase tracking-widest text-[#8B6F30] mb-1 font-semibold">
                    Advisor · {new Date(m.sent_at).toLocaleString()}
                  </div>
                )}
                {m.content}
              </div>
            ))}
            {!(lead.transcript || []).length && (
              <div className="text-xs text-slate-400">No transcript captured.</div>
            )}
          </div>
        </div>
      </div>
      <ReplyPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onSend={sendReply}
        subject={filled.subject}
        body={filled.body}
        to={lead.email}
        replyTo={advisorEmail}
        sending={sending}
      />
      <ReassignRequestModal
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        lead={lead}
        advisors={advisors}
      />
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}
